import React, { useState, useEffect } from "react";
import {
  Breadcrumb,
  Divider,
  Button,
  Card,
  Collapse,
  Form,
  Input,
  Row,
  Col,
  Space,
  Badge,
  Popconfirm,
  Select
} from "antd";
import * as Icon from "react-feather";
import { useDispatch } from 'react-redux';
import appLoaderAction from '../../redux/action/app-loader-action';
import notifyUtitls from '../../utils/notify-utils';
import CustomTable from '../../components/table/custom-table';
import { useCreatedSort, withCreatedColumn } from '../../shared/functional/created-sort';
import AddEditServiceLinkDialog from "./AddEditServiceLinkDialog";
import fileUtils from "../../utils/file-utils";
import { groupType, PREVIEW_TYPE } from "../../constants/app-constants";
import serviceLinkService from "../../services/service-link-service";
import userService from "../../services/user-service";
import { openWebsitePreview } from "../../utils/preview-utils";

const { Panel } = Collapse;
const { Option } = Select;

function callback(key) {
  console.log(key);
}

/**
 * NMU-546: the site shows Service Links in four fixed slots.
 *
 * The Academic & Admissions section places them by icon into programs /
 * admissions / scholarships / campus, so a fifth *active* link is saved, served
 * by the public API, and then dropped by the layout — configured but invisible,
 * with nothing on this page to say why. The count below is what makes the limit
 * visible before an editor runs into it; nmu-management-service enforces it
 * (ResponseStatus.SERVICE_LINK_LIMIT), which is what actually holds.
 *
 * Only active links count. An offline one is stored and never rendered, so it
 * costs nothing — a draft can still be parked below the limit.
 */
const MAX_ACTIVE_SERVICE_LINKS = 4;
const LIMIT_NOTICE = `* ระบบรองรับการแสดงผลลิงค์บริการสูงสุด ${MAX_ACTIVE_SERVICE_LINKS} รายการเท่านั้น`;

/** NMU-446: the four icons the Service Link form offers, drawn with react-feather. */
const ROW_ICON = {
  school: Icon.BookOpen,
  person_add: Icon.UserPlus,
  rewarded_ads: Icon.Award,
  apartment: Icon.Home,
};

const ICON_CELL_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  margin: "0 auto",
  borderRadius: 8,
  background: "#f5f5f5",
  overflow: "hidden",
};

const THUMBNAIL_STYLE = { width: "100%", height: "100%", objectFit: "cover" };

/**
 * Icon first, then a legacy uploaded image, then a placeholder — so the cell is
 * never blank whatever a record happens to carry.
 */
const renderRowIcon = (icon, serviceLinkId, attachImage) => {
  const IconComp = ROW_ICON[icon];
  if (IconComp) return <IconComp size={20} color="#1b341a" />;

  if (attachImage && attachImage.originalFilename) {
    return (
      <img
        alt=""
        style={THUMBNAIL_STYLE}
        src={fileUtils.getFileRenderUrl(
          groupType.SERVICE_LINK,
          serviceLinkId,
          attachImage.originalFilename
        )}
      />
    );
  }

  return <Icon.Image size={18} color="#bfbfbf" />;
};

const ServiceLink = () => {
  const dispatch = useDispatch();
  const [isShowAddFileDialog, setIsShowAddFileDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [condition, setCondition] = useState({ title: null, status: null });
  const [dataList, setDataList] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, rowPerPage: 10, rowCount: 0 });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [displayList, setDisplayList] = useState([]);
  const [userList, setUserList] = useState([]);
  // NMU-546: how many links are Online right now — the whole table, not the
  // filtered page, so a filter can never make the limit look further away.
  const [activeCount, setActiveCount] = useState(0);

  /*
    NMU-580: the วันที่/เวลาสร้าง column and the sort behind it.

    The server does the ordering — this table shows one page of many — so a
    click re-runs the search in the new order, back on page 1: the newest
    record is not on page 3 of the same list read the other way round.
  */
  const { sort, createdColumn, onChangeTable } = useCreatedSort((next) =>
    doSearch(1, { condition, sort: next }));

  const columns = [
    {
      title: "ชื่อลิงค์บริการ",
      dataIndex: "title",
      key: "name",
      render: (text) => <a>{text}</a>,
    },
    {
      title: "รูปภาพ",
      key: "image",
      align: "center",
      width: 90,
      /**
       * NMU-446: this cell was blank for every row. It only ever rendered
       * `attachImage`, and NMU-413 removed image upload from the service-link
       * form in favour of a fixed icon set — so nothing has an image any more
       * and the column showed nothing at all.
       *
       * The icon comes first now. A record from before NMU-413 may still carry
       * an image, so that is kept as a fallback rather than dropped, and a
       * record with neither gets a muted placeholder instead of an empty cell.
       */
      render: ({ serviceLinkId, attachImage, icon }) => (
        <div style={ICON_CELL_STYLE}>{renderRowIcon(icon, serviceLinkId, attachImage)}</div>
      ),
    },
    {
      title: "ลิงค์",
      dataIndex: "url",
      key: "url",
      render: (data) => {
        return (
          <a
            href="#"
            onClick={() => {
              window.open(
                data,
                'winname',
                'directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no'
              );
            }}>
            ลิงค์
          </a>
        );
      },
    },
    {
      title: 'จำนวนผู้เข้าชม',
      dataIndex: 'totalView',
      key: 'totalView',
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      render: (text) => (
        <Badge color={text ? '#4A9A0F' : '#CBCACA'} className="text-nowrap" text={text ? 'Online' : 'Offline'} />
      ),
    },
    {
      title: "ผู้บันทึก",
      key: "createBy",
      dataIndex: "createBy",
      render: (createBy) => {
        let check = userList.find((user) => createBy === user.userId);
        if (check) {
          return (`${check.profile?.firstName} ${check.profile?.lastName}`)
        }else{
          return (`undefined`)
        }
      },
    },
    {
      title: "ผู้แก้ไข",
      dataIndex: "updateBy",
      key: "updateBy",
      render: (updateBy) => {
        let check = userList.find((user) => updateBy === user.userId);
        if (check) {
          return (`${check.profile?.firstName} ${check.profile?.lastName}`)
        }else{
          return (`undefined`)
        }
      },
    },

    {
      title: 'ดำเนินการ',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (record) => (
        <Space size="middle">
          <a onClick={() => onClickEdit(record)} className="pe-3 border-end">
            <Icon.Edit size={18} />
          </a>

          <Popconfirm
            title="ยืนยันการลบข้อมูล ?"
            onConfirm={() => onConfirmDelete(record.serviceLinkId)}
            okText="ยืนยัน"
            cancelText="ยกเลิก" >
            <a >
              <Icon.Trash size={18} />
            </a>
          </Popconfirm>
        </Space>
      ),
    },
  ];


  useEffect(() => {
    async function initData() {
      const response = await userService.search();
      setUserList(response.list);
    }
    initData();
    doSearch(1);
    doSearchDisplay();
    countActiveLinks();
  }, []);

  useEffect(() => {
    if (editData) {
      onEdit(editData);
    }
  }, [editData]);

  const onAdd = () => {
    // NMU-546: belt and braces — the button is disabled at the limit, but a
    // count that arrived late (or failed) must not open a form whose save can
    // only be rejected.
    if (isLimitReached) {
      notifyUtitls.warning('ไม่สามารถเพิ่มรายการได้', LIMIT_NOTICE.replace('* ', ''));
      return;
    }
    setEditData(null);
    setIsShowAddFileDialog(true);
  };

  const onCancel = () => {
    setIsShowAddFileDialog(false);
    setEditData(null);
  };

  const onSaveComplete = () => {
    setEditData(null);
    setIsShowAddFileDialog(false);
    doSearch(1);
    countActiveLinks();
  };

  const onEdit = (data) => {
    setEditData(data);
    setIsShowAddFileDialog(true);
  }

  const onChangeCondition = (event) => {
    const { name, value } = event.target;
    setCondition({ ...condition, [name]: value });
  }

  const onChangePage = (currentPage) => {
    doSearch(currentPage);
  }

  /**
   * @param currentPage page to land on, 1 to start over.
   * @param overrides   NMU-529: search with these filters instead of the ones in
   *   state. setCondition only takes effect on the next render, so a caller that
   *   resets the filters and searches in the same handler would otherwise re-run
   *   the search it just cleared.
   */
  const doSearch = async (currentPage, overrides) => {
    try {
      const activeSort = overrides && overrides.sort ? overrides.sort : sort;
      const newPaginate = {
        ...pagination,
        currentPage: currentPage ? currentPage : pagination.currentPage,
        // NMU-580: the order travels with the page — see created-sort.js.
        ...activeSort,
      };

      dispatch(appLoaderAction.enable());

      const activeCondition = overrides ? overrides.condition : condition;

      // NMU-579: keywordList is no longer a search condition. The backend skips
      // the criterion when it is absent, so nothing else changes.
      const newCondition = { title: activeCondition.title, status: activeCondition.status, createBy: activeCondition.createBy }
      const result = await serviceLinkService.search(newCondition, newPaginate);

      setDataList(result.list);
      setPagination(result.pagination ? result.pagination : newPaginate);
      setSelectedRowKeys([]);
      setSelectedRows([]);

      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      dispatch(notifyUtitls.error(err));
    }
  };

  /**
   * NMU-529: clearing the filters has to re-run the search, not just empty the
   * inputs — otherwise the table keeps showing the previous result set while the
   * form claims no filter is applied. Back to page 1, since the record the user
   * was looking at on page 3 of a filtered list is somewhere else entirely in
   * the full one.
   */
  const onClearSearch = () => {
    const cleared = { title: null, status: null, createBy: null };
    setCondition(cleared);
    doSearch(1, { condition: cleared });
  }

  const onClickEdit = (record) => {
    try {
      dispatch(appLoaderAction.enable());
      setEditData(record);
      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  }

  const onConfirmDelete = (id) => {
    onDelete(id);
  }

  const onDelete = async (id) => {
    try {
      dispatch(appLoaderAction.enable());
      await serviceLinkService.remove({ deleteIds: [id] });
      dispatch(appLoaderAction.disale());
      notifyUtitls.deleted('สำเร็จ', 'ลบข้อมูลสำเร็จ');
      doSearch(1);
      countActiveLinks();
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  };

  const rowSelection = () => {
    return {
      onChange: (selectedRowKeys, selectedRows) => {
        setSelectedRows(selectedRows);
        setSelectedRowKeys(selectedRowKeys);
      },
      selectedRowKeys,
    };
  }

  /**
   * NMU-546: count the Online links, independent of the filters above.
   *
   * Asks for a single row and reads `rowCount` off the pager rather than
   * fetching every record to measure the list — the answer wanted here is the
   * total, and the table's own search is already paged.
   */
  const countActiveLinks = async () => {
    try {
      const result = await serviceLinkService.search(
        { title: null, status: true, createBy: null },
        { currentPage: 1, rowPerPage: 1 },
      );
      setActiveCount(result.pagination?.rowCount ?? 0);
    } catch (err) {
      // A failed count must not block the page; the server-side check still
      // rejects a fifth link, so the button stays usable rather than stuck.
      console.error('countActiveLinks', err);
    }
  };

  const doSearchDisplay = async () => {
    try {
      dispatch(appLoaderAction.enable());

      const result = await serviceLinkService.getDisplay();

      setDisplayList(result.list);
      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      dispatch(notifyUtitls.error(err));
    }
  };

  /*
   * NMU-445: onAddToDisplay / onSaveDisplay / onClearDisplay / onRemoveDisplay
   * and onSortEnd went with the arrangement panel — nothing can reach them now.
   * displayList itself stays: it is still fetched on mount and is what the
   * "ดูตัวอย่างเว็บไซต์" preview posts.
   */

  const onPreview = async () => {
    try {
      dispatch(appLoaderAction.enable());

      // dispatch(serviceLinkPreviewAction.addServiceLinkPreview(displayList));

      await openWebsitePreview({ type: PREVIEW_TYPE.serviceLink, listBean: displayList });
      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  }

  const renderOptionUser = () => {
    return userList?.map((item) => {
      if (item?.profile) {
        const profile = item.profile;
        return (
          <Select.Option value={item.userId} key={item.userId}>
            {`${profile.firstName} ${profile.lastName}`}
          </Select.Option>
        );
      } else {
        return null
      }
    });
  };

  const onChangeSelect = (name) => {
    return (value) => {
      setCondition({ ...condition, [name]: value });
    };
  }

  const isLimitReached = activeCount >= MAX_ACTIVE_SERVICE_LINKS;


  return (
    <div>
      <h2 className="color-primary-title">ลิงค์บริการ</h2>
      <Breadcrumb>
        <Breadcrumb.Item>การจัดการเนื้อหา</Breadcrumb.Item>
        <Breadcrumb.Item>ลิงค์บริการ</Breadcrumb.Item>
      </Breadcrumb>
      <Divider className="mt-2 mb-4" />
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <Button
            id="add-service-link"
            type="primary"
            disabled={isLimitReached}
            onClick={() => onAdd()}>
            <Icon.Plus className="me-1" size={18} /> เพิ่มลิงค์บริการ
          </Button>
          {/*
            NMU-546: the limit is stated whether or not it has been hit — an
            editor about to add the fourth link should know it is the last one,
            not discover it on the fifth. It turns amber once adding is closed
            off, which is the only state where the sentence is a refusal rather
            than a note.
          */}
          <div
            id="service-link-limit-notice"
            className="mt-2 small"
            style={{ color: isLimitReached ? '#B7791F' : '#8C8C8C' }}>
            {LIMIT_NOTICE}
            {` (ใช้งานอยู่ ${activeCount}/${MAX_ACTIVE_SERVICE_LINKS} รายการ)`}
          </div>
        </div>
        <div>
          <Button className="mx-2 btn-line" onClick={() => onPreview()}>
            <Icon.Eye className="me-1" size={18} /> ดูตัวอย่างเว็บไซต์
          </Button>
        </div>
      </div>
      <Card className="mt-4" bordered={false}>
        <Collapse className="mb-4" defaultActiveKey={["1"]} onChange={callback}>
          <Panel header="ตัวกรอง" key="1">
            <div>
              <Form layout="vertical">
                <Row gutter={20}>
                  <Col span={8}>
                    <Form.Item label="ชื่อลิงค์บริการ" className="mb-0">
                      <Input
                        name="title"
                        value={condition.title}
                        onChange={onChangeCondition}
                      />
                    </Form.Item>
                  </Col>
                  {/* NMU-579: no คีย์เวิร์ด filter here. Keywords are something
                      an editor sets on a link in the add/edit dialog — they were
                      never a useful way to find one. The dialog keeps its
                      keyword input. */}
                  <Col span={8}>
                    <Form.Item label="ผู้บันทึก" className="mb-0">
                      <Select
                        showSearch
                        placeholder="Search to Select"
                        optionFilterProp="children"
                        allowClear
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        filterSort={(optionA, optionB) =>
                          optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
                        }
                        value={condition.createBy}
                        onChange={onChangeSelect('createBy')}
                      >
                        {renderOptionUser()}

                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="สถานะ" className="mb-0">
                      <Select name="status" value={condition.status} onChange={onChangeSelect('status')}>
                        <Option value={null}>All</Option>
                        <Option value={true}>Online</Option>
                        <Option value={false}>Offline</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={20}>
                  <Col className="mt-4">
                    <Button type="primary" className="w-100" onClick={() => doSearch(1)}>
                      <Icon.Search className="me-1" size={18} /> ค้นหา
                    </Button>
                  </Col>
                  <Col className="mt-4">
                    <Button className="btn-gray" onClick={() => onClearSearch()}>ล้างค่า</Button>
                  </Col>
                </Row>
              </Form>
            </div>
          </Panel>
        </Collapse>
        {/*
          NMU-445: the "การแสดงผลลิงค์บริการ" arrangement is gone. The website
          no longer reads it — the public service-link API returns the published
          list and the homepage sections pick from it by icon (NMU-442), so this
          panel only ever edited a document nothing consumed. The filter and the
          table below are untouched.
        */}

        <CustomTable
          rowSelection={{
            type: 'checkbox',
            ...rowSelection(),
          }}
          rowKey="serviceLinkId"
          tableName="serviceLinkTable"
          columns={withCreatedColumn(columns, createdColumn)}
          onChangeTable={onChangeTable}
          data={dataList}
          scroll={{ x: true }}
          isPagination={true}
          onChangePage={onChangePage}
          currentPage={pagination.currentPage}
          pageSize={pagination.rowPerPage}
          totalRecord={pagination.rowCount}
        />
      </Card>
      <AddEditServiceLinkDialog
        isShow={isShowAddFileDialog}
        editData={editData}
        onCancel={onCancel}
        onSaveComplete={onSaveComplete}></AddEditServiceLinkDialog>
    </div>
  );
};

export default ServiceLink;
